<template>
  <div class="access-page">
    <div class="container">
      <!-- Coluna esquerda -->
      <div class="left">
        <h1>Acesso ao Sistema</h1>
        <p class="subtitle">
          O acesso ao sistema possui <strong>2 formas simples</strong>:
        </p>

        <!-- Navegador -->
        <div class="card">
          <span class="indicator"></span>
          <div class="card-content">
            <h3>Navegador</h3>
            <p>
              O acesso é através do link:
              <a
                href="https://painel.salesteams.com.br"
                target="_blank"
                rel="noopener"
              >
                https://painel.salesteams.com.br
              </a>
            </p>
          </div>
        </div>

        <!-- Aplicativo -->
        <div class="card">
          <span class="indicator"></span>
          <div class="card-content">
            <h3>Aplicativo</h3>
            <p>
              Clique na loja correspondente ao sistema operacional do seu
              celular para baixar o aplicativo.
            </p>
          </div>
        </div>

        <!--
          Aviso de migração. Fica escondido até `MIGRACAO_ATIVA` virar true, no
          dia em que o aplicativo oficial estiver publicado nas duas lojas.
          Dentro do próprio aplicativo ele não aparece.
        -->
        <div v-if="migracaoAtiva && !dentroDoApp" class="card migration-card">
          <span class="indicator"></span>
          <div class="card-content">
            <h3>Novo aplicativo oficial</h3>
            <p>
              Lançamos o aplicativo <strong>oficial</strong> do Sales Teams.
              Se você já tem um aplicativo antigo instalado, baixe o novo pelos
              botões ao lado e faça login uma vez — a partir daí ele continua
              conectado.
            </p>
            <p class="migration-note">
              O aplicativo antigo continua funcionando por enquanto, mas as
              novidades saem apenas no oficial.
            </p>
          </div>
        </div>
      </div>

      <!-- Coluna direita -->
      <div class="right">
        <!-- tabindex + keydown: sem eles o role="button" e apenas decorativo,
             o cartao nao recebe foco e nao e acionavel por teclado. -->
        <div
          class="store-card"
          role="button"
          tabindex="0"
          aria-label="Baixar na Play Store"
          @click="goTo(playStore)"
          @keydown.enter.prevent="goTo(playStore)"
          @keydown.space.prevent="goTo(playStore)"
        >
          <img
            src="@/assets/images/playstore.png"
            alt="Google Play Store"
          />
        </div>

        <div
          class="store-card"
          role="button"
          tabindex="0"
          aria-label="Baixar na App Store"
          @click="goTo(appStore)"
          @keydown.enter.prevent="goTo(appStore)"
          @keydown.space.prevent="goTo(appStore)"
        >
          <img
            src="@/assets/images/aplestore.png"
            alt="Apple App Store"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import native from "@/native";

// =============================================================================
// APLICATIVO OFICIAL — TROCAR NO DIA DA PUBLICACAO
// =============================================================================
//
// Os links ATIVOS abaixo ainda apontam para o aplicativo publicado por um
// TERCEIRO (conta "converta"). Nao temos acesso a essa conta, ao codigo nem ao
// keystore, e nunca teremos — por isso o aplicativo proprio e um lancamento
// novo, com bundle id novo (`br.com.salesteams.app`).
//
// NAO troque agora. Enquanto o aplicativo oficial nao estiver PUBLICADO E
// APROVADO nas duas lojas, apontar para ele daria erro 404 na loja — o que e
// pior do que continuar mandando para o aplicativo antigo, que funciona (aponta
// para a mesma API).
//
// NO DIA DA PUBLICACAO, faca exatamente duas coisas:
//   1. troque os valores de `playStore` e `appStore` pelos de APP_OFICIAL
//      (o id numerico da App Store sai do App Store Connect depois de criado o
//      registro do aplicativo);
//   2. troque `MIGRACAO_ATIVA` para `true` — isso liga o aviso de migracao
//      nesta tela.
//
// O aviso e a troca andam JUNTOS de proposito: aviso apontando para link velho
// confunde mais do que ajuda.
// =============================================================================
const APP_OFICIAL = {
  playStore:
    "https://play.google.com/store/apps/details?id=br.com.salesteams.app&hl=pt_BR",
  // Substituir `id0000000000` pelo Apple ID real do aplicativo.
  appStore: "https://apps.apple.com/br/app/id0000000000",
};

const APP_ANTIGO_DE_TERCEIRO = {
  playStore:
    "https://play.google.com/store/apps/details?id=com.converta.salesteams&hl=pt_BR",
  appStore: "https://apps.apple.com/br/app/sales-teams/id6744412883",
};

const MIGRACAO_ATIVA = false;

export default {
  name: "AccessPlatform",
  data() {
    const lojas = MIGRACAO_ATIVA ? APP_OFICIAL : APP_ANTIGO_DE_TERCEIRO;

    return {
      playStore: lojas.playStore,
      appStore: lojas.appStore,
      migracaoAtiva: MIGRACAO_ATIVA,
      // Dentro do aplicativo oficial nao faz sentido mostrar "baixe o
      // aplicativo": a pessoa ja esta nele.
      dentroDoApp: false,
    };
  },
  mounted() {
    this.dentroDoApp = native.isNative;
  },
  methods: {
    goTo(url) {
      // `native.openExternal` abre no navegador do sistema quando estamos
      // dentro do aplicativo (a loja nao pode abrir dentro do WebView) e cai em
      // `window.open` no navegador comum.
      native.openExternal(url);
    },
  },
};
</script>

<style scoped>
.access-page {
  min-height: 100vh;
  background: var(--st-surface);
  display: flex;
  align-items: center;
  padding: 32px 16px;
}

.container {
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 48px;
}

.migration-card {
  border-left: 4px solid var(--st-brand);
  background: var(--st-brand-soft);
}

.migration-note {
  font-size: 13px;
  opacity: 0.75;
  margin-bottom: 0;
}

/* LEFT */
.left h1 {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 12px;
}

.subtitle {
  font-size: 16px;
  color: var(--st-text-muted);
  margin-bottom: 32px;
}

.card {
  display: flex;
  background: var(--st-surface);
  border-radius: var(--st-radius-lg);
  box-shadow: var(--st-shadow-md);
  padding: 20px 24px;
  margin-bottom: 24px;
}

.indicator {
  width: 6px;
  border-radius: 4px;
  background: var(--st-brand);
  margin-right: 16px;
}

.card-content h3 {
  margin: 0 0 8px;
  font-size: 18px;
}

.card-content p {
  margin: 0;
  font-size: 15px;
  color: var(--st-text-muted);
}

.card-content a {
  color: var(--st-brand);
  font-weight: 600;
  text-decoration: none;
}

.card-content a:hover {
  text-decoration: underline;
}

/* RIGHT */
.right {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 32px;
}

.store-card {
  background: var(--st-canvas);
  border-radius: var(--st-radius-xl);
  padding: 24px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.store-card img {
  max-width: 220px;
  width: 100%;
}

.store-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--st-shadow-lg);
}

.store-card:focus-visible {
  outline: 2px solid var(--st-focus-ring);
  outline-offset: 3px;
}

/* RESPONSIVO */
@media (max-width: 900px) {
  .container {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .right {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
  }

  .store-card {
    width: 100%;
    max-width: 280px;
  }
}
</style>
